import React, { useCallback, useEffect, useMemo, useState } from "react";
import AsyncSelect from "react-select/async";
import { FormBuilder } from "react-reactive-form";
import { useCsrDropdownData } from "../hooks/useCsrDropdownData";

const DEFAULT_PLACEHOLDER = "Type to search from list or select from the list";
const DEFAULT_NO_OPTIONS = "No matches found";
const DEFAULT_LOADING = "Loading data...";

const formatNames = names => {
  if (!Array.isArray(names)) return [];
  return names
    .filter(item => item && typeof item === "string")
    .map(item => ({ value: item, label: item }));
};

const filterOptions = (options, inputValue) => {
  if (!inputValue) return options;
  const search = inputValue.toLowerCase();
  return options.filter(option => option.label.toLowerCase().includes(search));
};

const customStyles = disabled => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: "38px",
    border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
    borderRadius: "0.375rem",
    boxShadow: state.isFocused
      ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
      : "none",
    "&:hover": {
      border: "1px solid #86b7fe",
    },
    opacity: disabled ? 0.6 : 1,
    pointerEvents: disabled ? "none" : "auto",
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: "#e9ecef",
    borderRadius: "0.25rem",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#495057",
    fontSize: "0.875rem",
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: "#6c757d",
    "&:hover": {
      backgroundColor: "#dc3545",
      color: "white",
    },
  }),
  menu: provided => ({
    ...provided,
    zIndex: 9999,
  }),
  container: provided => ({
    ...provided,
    width: "100%",
  }),
});

const PlainTableMultiSelectDropdown = ({
  controlName,
  formGroup,
  disabled = false,
  // studyId is intentionally accepted but unused — data comes from Redux,
  // owned by the orchestrator (startStudyDataLoad on the wizard page).
  studyId, // eslint-disable-line no-unused-vars
  placeholder = DEFAULT_PLACEHOLDER,
  noOptionsMessage = DEFAULT_NO_OPTIONS,
  loadingMessage = DEFAULT_LOADING,
  tables = true,
}) => {
  const { items, isLoading, isReady } = useCsrDropdownData({
    kind: tables ? "table" : "figure",
  });

  const options = useMemo(() => formatNames(items), [items]);

  const loadOptions = useCallback(
    (inputValue = "") => Promise.resolve(filterOptions(options, inputValue)),
    [options]
  );

  useEffect(() => {
    if (
      formGroup &&
      typeof formGroup.contains === "function" &&
      !formGroup.contains(controlName)
    ) {
      formGroup.addControl(controlName, FormBuilder.control([]));
    }
  }, [formGroup, controlName]);

  const [formValue, setFormValue] = useState(() => {
    const formControl = formGroup?.get && formGroup.get(controlName);
    return formControl && Array.isArray(formControl.value)
      ? formControl.value
      : [];
  });

  useEffect(() => {
    const formControl = formGroup?.get && formGroup.get(controlName);
    if (!formControl) {
      return;
    }

    const initialValue = Array.isArray(formControl.value)
      ? formControl.value
      : [];
    setFormValue(initialValue);

    const subscription = formControl.valueChanges?.subscribe(newValue => {
      const nextValue = Array.isArray(newValue) ? newValue : [];
      setFormValue(nextValue);
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [formGroup, controlName]);

  const formControl = formGroup?.get && formGroup.get(controlName);
  if (!formControl) {
    return null;
  }

  const currentValue = Array.isArray(formValue) ? formValue : [];
  const normalizedValue = currentValue.filter(
    item => typeof item === "string" && item.trim() !== ""
  );
  const selectedOptions = normalizedValue.map(item => ({
    value: item,
    label: item,
  }));

  return (
    <AsyncSelect
      // Force remount once Redux options arrive so AsyncSelect's cached
      // empty defaultOptions are replaced with the real list. Without this,
      // a hard refresh that lands on a wizard page renders the dropdown
      // before the slice is hydrated, and AsyncSelect keeps showing empty.
      key={`${controlName}-${options.length}`}
      isMulti
      closeMenuOnSelect={false}
      value={selectedOptions}
      loadOptions={loadOptions}
      defaultOptions={options}
      isLoading={isLoading && !isReady}
      onChange={opts => {
        const nextValue = Array.isArray(opts)
          ? opts
              .filter(option => option && typeof option.value === "string")
              .map(option => option.value)
          : [];
        formControl.setValue(nextValue);
        setFormValue(nextValue);
        if (formControl.markAsTouched) {
          formControl.markAsTouched();
        }
        if (formControl.markAsDirty) {
          formControl.markAsDirty();
        }
      }}
      styles={customStyles(disabled)}
      placeholder={placeholder}
      isClearable
      isSearchable
      isDisabled={disabled}
      className="plain-table-multi-select"
      classNamePrefix="plain-table-multi-select"
      noOptionsMessage={({ inputValue }) =>
        inputValue ? noOptionsMessage : placeholder
      }
      loadingMessage={() => loadingMessage}
      cacheOptions
    />
  );
};

export default PlainTableMultiSelectDropdown;
